/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */

import React from "react"
import ReferralWrapper from "./src/ReferralWrapper"
import { navigate } from "gatsby"

import "./src/styles/global.css"
import "react-datepicker/dist/react-datepicker.css"
import "./src/styles/wpbr-public-main.css"
import "./src/styles/custome.css"
import "./src/styles/front-page.css"
import "./src/styles/responsive.css"

export const wrapRootElement = ReferralWrapper

/**
 * The MOCK SITE (docs/mock-site.md, src/mock/). Both hooks compare the build
 * flag as a LITERAL at the import site, as src/pages/switch.tsx does: Gatsby
 * inlines `process.env.GATSBY_MOCK_MODE` (gatsby-node.ts folds an absent one
 * to ""), webpack sees a constant-false branch, and the `import()` — the
 * fetch router, the fixtures, the panel — is never built into a bundle
 * without the flag.
 *
 * onClientEntry runs BEFORE the first render, and Gatsby waits for the
 * promise it returns, so `window.fetch` is already the mock's and the fake
 * Chargebee SDK is already on the page by the time any effect fires.
 */
export const onClientEntry = () => {
  if (process.env.GATSBY_MOCK_MODE === "true") {
    return import("./src/mock/install").then((m) => m.installMock())
  }
}

// `?scenario=` comes off the URL only once the router has read it; see
// stripScenarioParam in src/mock/install.ts.
export const onInitialClientRender = () => {
  if (process.env.GATSBY_MOCK_MODE === "true") {
    import("./src/mock/install").then((m) => m.stripScenarioParam())
  }
}

// The floating "Mock" button and its panel, on every page. MockMount renders
// null on the server and on the first client render (so SSR and hydration
// agree), then lazy-loads the panel — gatsby-ssr.js wraps identically.
export const wrapPageElement = ({ element }) => {
  if (process.env.GATSBY_MOCK_MODE !== "true") return element
  const MockMount = require("./src/mock/MockMount").default
  return (
    <>
      {element}
      <MockMount />
    </>
  )
}

/**
 * Route-change scroll. global.css sets `html { scroll-behavior: smooth }` for
 * in-page anchors, which also turns Gatsby's own scroll-to-top on navigation
 * into an animation. On a slow phone the new page paints at the OLD offset
 * and the animation stalls or gets cut short by layout, so a link "works" but
 * lands you mid-page. Jump instantly instead: saved position for back/forward,
 * the anchor for hash links, the top for everything else.
 */
export const shouldUpdateScroll = ({ routerProps: { location }, getSavedScrollPosition }) => {
  if (typeof window === "undefined") return false
  if (location.hash) {
    const target = document.getElementById(location.hash.slice(1))
    if (target) {
      target.scrollIntoView({ behavior: "instant", block: "start" })
      return false
    }
    return true // not rendered yet — let Gatsby retry its own way
  }
  const [x, y] = getSavedScrollPosition(location) || [0, 0]
  window.scrollTo({ top: y, left: x, behavior: "instant" })
  return false
}

export const onRouteUpdate = ({ location }) => {
  // Redirect unknown /app/* paths to the /app index
  const { apps } = require("./src/data/appData")
  const knownAppPaths = ["/app", ...apps.map(a => `/app/${a.slug}`)]
  const normalizedPath = location.pathname.replace(/\/$/, "")
  if (normalizedPath.startsWith("/app") && !knownAppPaths.includes(normalizedPath)) {
    navigate("/app")
    return
  }

  const session = JSON.parse(localStorage.getItem("authFlag"))
  const isValidSession =
    session && session?.valid && new Date().getTime() < session?.expires

  if (process.env.GATSBY_ENVIRONMENT === "DEV") {
    const isAuthenticated =
      typeof window !== "undefined" && localStorage.getItem("authFlag")

    // Gate every page behind the staging login, but never redirect while we're
    // already on /login — doing so re-triggers onRouteUpdate and loops forever
    // (the page visibly "blinks"). login.tsx handles sending an authenticated
    // user off /login back to the site.
    // Compare against normalizedPath, NOT location.pathname: Gatsby 5 defaults to
    // trailingSlash: 'always', so pathname is "/login/" and a bare "/login" check
    // never matches — which is exactly the loop this guard exists to prevent.
    if (normalizedPath !== "/login" && (!isAuthenticated || !isValidSession)) {
      navigate("/login")
    }
  }
}
